import { InfoCircledIcon } from '@radix-ui/react-icons'
import { TextField } from '@radix-ui/themes'
import { TooltipWrapper } from 'components/TooltipWrapper'
import { format } from 'date-fns'
import React from 'react'
import styled from 'styled-components'

import {
  InputAndSubtitle,
  StyledInputContainer,
  StyledLabel,
  SubtitleRowStyled,
  SubtitleTextStyled,
  TextValue,
} from './InputStyles'

export interface DateInputProps
  extends React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  $label?: string
  $labelColor?: string
  $labelLocation?: 'top' | 'side'
  $subtitleText?: string
  $subtitleTextColor?: string
  $backgroundColor?: string
  $borderColor?: string
  mode?: 'read' | 'edit'
  ref?: React.ForwardedRef<HTMLInputElement>
}

const InputStyled = styled(TextField.Root)<DateInputProps>`
  display: flex;
  align-items: center;
  position: relative;
  font-family: ${({ theme }) => theme.fontFamily.manrope};
  color: ${({ theme }) => theme.forecast.defaultText};
  background-color: ${({ $backgroundColor, theme }) =>
    $backgroundColor ? $backgroundColor : theme.forecast.cardGroupBackground};
  border: ${(props) =>
    props.$borderColor
      ? `0.0625rem solid ${props.$borderColor}`
      : '0.0625rem solid transparent'};
  border-radius: 0.25rem;
  font-weight: 600;
  box-shadow: none !important;
  opacity: 1;

  &:hover {
    cursor: pointer;
  }

  &:focus {
    cursor: initial;
  }

  .rt-TextFieldInput {
    display: inline;
    padding: 0.5rem;
  }
`

const formatDateLabel = (dateString: string): string => {
  // Check for YYYY-MM-DD format
  const simpleDateFormat = /^\d{4}-\d{2}-\d{2}$/
  // Check for ISO format (YYYY-MM-DDTHH:mm:ss.sssZ)
  const isoDateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/

  if (simpleDateFormat.test(dateString)) {
    const [year, month, day] = dateString.split('-')
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
    return format(date, 'EEE MMM dd yyyy')
  }

  if (isoDateFormat.test(dateString)) {
    const date = new Date(dateString)
    return format(date, 'EEE MMM dd yyyy')
  }

  return dateString
}

const InfoIconWrapper = styled.span`
  display: flex;
  align-items: center;
  cursor: pointer;
  svg {
    width: 1rem;
    height: 1rem;
    color: ${({ theme }) => theme.forecast.defaultText};
  }
`

const InputWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  (props, ref) => {
    const {
      $label,
      $labelColor,
      $labelLocation,
      $subtitleText,
      $subtitleTextColor,
      mode,
    } = props

    return (
      <StyledInputContainer $labelLocation={$labelLocation}>
        {$label && (
          <StyledLabel $labelColor={$labelColor} htmlFor={$label}>
            {$label}
          </StyledLabel>
        )}
        <InputWithIcon>
          {props.defaultValue && (
            <TooltipWrapper
              side='top'
              offset={2}
              label={'All dates are saved in EST (-05:00)'}>
              <InfoIconWrapper>
                <InfoCircledIcon />
              </InfoIconWrapper>
            </TooltipWrapper>
          )}
          <InputAndSubtitle>
            {mode === 'read' ? (
              <TextValue>
                {formatDateLabel(props.defaultValue as string)}
              </TextValue>
            ) : (
              <InputStyled
                {...props}
                ref={ref}
                placeholder='YYYY-MM-DD'
                type='date'></InputStyled>
            )}
            {mode !== 'read' && $subtitleText && (
              <SubtitleRowStyled>
                <SubtitleTextStyled $subtitleTextColor={$subtitleTextColor}>
                  {$subtitleText}
                </SubtitleTextStyled>
              </SubtitleRowStyled>
            )}
          </InputAndSubtitle>
        </InputWithIcon>
      </StyledInputContainer>
    )
  }
)
DateInput.displayName = 'DateInput'
